import React, { useEffect } from 'react'
import useDebounce from 'helpers/debounce'
import InitiativeSubmissionService from './Service'
import { IInitiativeSubmissionProviderProps, IInitiativeSubmissionViewProps } from './Interfaces'
import SessionManagerService from 'smart-components/session-manager/Service'
import { useNotification, useServiceState } from 'helpers'
import InitiativeSelectorDialogService from '../initiatives-selector-dialog/Service'
import UserResponsesManagerService from 'smart-components/user-responses-manager/Service'
import { AUTHENTICATION_SERVICE } from 'config/constants/env'

const Provider: React.FC<IInitiativeSubmissionProviderProps> = ({ enterpriseId, code, children }) => {
  const serviceState = useServiceState(InitiativeSubmissionService)
  const { emitError } = useNotification()

  const debouncedAnswers = useDebounce(serviceState.answers, 500)

  async function onSubmitAnswers() {
    const [user, error] = await InitiativeSubmissionService.submitInitiativeAnswers()

    if (error) {
      emitError(error)
    } else if (user) {
      SessionManagerService.setCurrentUser(user)
      InitiativeSelectorDialogService.loadInitiativeOptions(UserResponsesManagerService.state.userResponses)
    }
  }

  useEffect(() => {
    async function loadInitiativeInfo() {
      if (!serviceState.isLoadingInitiative) {
        const error = await InitiativeSubmissionService.loadInitiativeInformation(enterpriseId, code)
        if (error) {
          window.location.href = `${AUTHENTICATION_SERVICE.WEB_URL}/enterprise/invalid-invitation-link`
        }
      }
    }
    loadInitiativeInfo()
  }, [enterpriseId, code])

  useEffect(() => {
    async function saveData() {
      const error = await InitiativeSubmissionService.saveInitiativeSubmission()
      if (error) {
        emitError(error)
      }
    }
    if (!serviceState.isSavingInitiativeData && !serviceState.isLoadingInitiative && serviceState.initiativeData) {
      saveData()
    }
  }, [debouncedAnswers])

  useEffect(() => {
    const timer = setInterval(() => {
      InitiativeSubmissionService.setCurrentDateTime()
    }, 6000)
    return () => {
      clearInterval(timer)
    }
  }, [])

  const viewProps: IInitiativeSubmissionViewProps = {
    state: serviceState,
    onSubmitAnswers,
    onSaveInitiativeDraft: async () => {
      const error = await InitiativeSubmissionService.saveInitiativeSubmission()
      if (error) {
        emitError(error)
      }
    },
    setGpaGrade: (grade: 'GRADUATE' | 'UNDERGRADUATE' | 'DOES_NOT_APPLY') =>
      InitiativeSubmissionService.setGpaGrade(grade),
    setGpaValue: (input: string) => InitiativeSubmissionService.setGpaValue(input),
    setQuestionAnswer: (id: number, answer: string) => InitiativeSubmissionService.setQuestionAnswer(id, answer),
    setQuestionChoice: (id: number, choices: number[]) => InitiativeSubmissionService.setQuestionChoice(id, choices),
    toggleSuccessDialog: () => InitiativeSubmissionService.toggleIsSuccessDialogOpen(),
    toggleIncompleteProfileDialog: () => InitiativeSubmissionService.toggleIsIncompleteProfileDialogOpen(),
    toggleValidationError: () => InitiativeSubmissionService.toggleValidationError()
  }

  return <React.Fragment>{children(viewProps)}</React.Fragment>
}

export default Provider
